import React, { useMemo } from 'react';
import { f0, fmtDate, computeMatchRows } from './matchChronicShared';

// Einstein Phase 1, Task 3 proof-of-concept — a standalone, presentational slice of
// MatchChronic.js's individual-view report. Takes a player id plus the same reference datasets
// MatchChronic.js already fetches (players/opponents/scenarios/customTemplates/positions/
// allMatchDrills) as props instead of fetching them itself, and renders through the exact same
// computeMatchRows() pure function MatchChronic.js uses — no duplicated business logic, no
// recomputation of anything. This is what a `compose` tier response assembles multiple of: one
// instance per resolved player, sharing one upstream fetch of the reference data across N panels
// instead of each panel re-fetching it.
export default function MatchChronicPanel({ playerId, players, opponents, scenarios, customTemplates, positions, allMatchDrills, limit = 5 }) {
  const player = useMemo(() => players.find(p => String(p.id) === String(playerId)), [players, playerId]);

  const playerDrills = useMemo(() => {
    if (!player?.name) return [];
    const nameLower = player.name.toLowerCase();
    return allMatchDrills.filter(d => (d.player_name || '').toLowerCase() === nameLower);
  }, [allMatchDrills, player]);

  const rows = useMemo(
    () => computeMatchRows(playerDrills, player, opponents, scenarios, customTemplates, positions).slice(0, limit),
    [playerDrills, player, opponents, scenarios, customTemplates, positions, limit]
  );

  if (!player) {
    return <div style={{ padding: 16, color: 'var(--text-dimmed)', fontSize: 13 }}>No player found for id {playerId}.</div>;
  }

  return (
    <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 12, padding: '18px 20px', minWidth: 320 }}>
      <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 2 }}>{player.name}</div>
      <div style={{ fontSize: 11, color: 'var(--text-dimmed)', marginBottom: 14 }}>Last {limit} matches — Chronic Match Report</div>
      {rows.length === 0 ? (
        <div style={{ color: 'var(--text-dimmed)', fontSize: 12, padding: '16px 0' }}>No match sessions found.</div>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr style={{ textAlign: 'right', color: 'var(--text-dimmed)', fontSize: 10, textTransform: 'uppercase' }}>
              <th style={{ textAlign: 'left', padding: '4px 8px' }}>Opponent</th>
              <th style={{ padding: '4px 8px' }}>Date</th>
              <th style={{ padding: '4px 8px' }}>TD</th>
              <th style={{ padding: '4px 8px' }}>HMLD</th>
              <th style={{ padding: '4px 8px' }}>Mins</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.sessionId} style={{ borderTop: '1px solid var(--border)' }}>
                <td style={{ textAlign: 'left', padding: '6px 8px', color: 'var(--text-primary)' }}>{r.opponent}</td>
                <td style={{ textAlign: 'right', padding: '6px 8px', color: 'var(--text-muted)' }}>{fmtDate(r.date)}</td>
                <td style={{ textAlign: 'right', padding: '6px 8px', color: 'var(--text-secondary)' }}>{f0(r.td)}m</td>
                <td style={{ textAlign: 'right', padding: '6px 8px', color: 'var(--text-secondary)' }}>{f0(r.hmld)}m</td>
                <td style={{ textAlign: 'right', padding: '6px 8px', color: 'var(--text-muted)' }}>{f0(r.mins)}'</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
